import { useGraphQLQuery } from '@betterment/graphql';
import { CenteredLayout, H1, IconLink, Logo, PancakeStack, Separator, WorkflowHeader } from '@betterment/samba-react';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { useCurrentEmployeeId } from '~/b4b/utils/currentEmployee';
import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { LoadedViewContent } from './FourOhOneKContributionsEditView/LoadedViewContent';
import { LoadingViewContent } from './FourOhOneKContributionsEditView/LoadingViewContent';
import {
  EmployeeContributionRatesDocument,
  EmployeeContributionRatesQuery,
} from './graphql/query.employeeContributionRates.generated';

export type Props = {
  employeeName: string;
  isEnrolledInAutoEscalation: boolean;
  hasOutboundPayrollIntegration: boolean;
  payrollProviderName: string;
};

export const FourOhOneKContributionsEditView = ({
  employeeName,
  isEnrolledInAutoEscalation,
  hasOutboundPayrollIntegration,
  payrollProviderName,
}: Props) => {
  const employerId = useCurrentEmployerId();
  const employeeId = useCurrentEmployeeId();

  const { data: rawData } = useGraphQLQuery(EmployeeContributionRatesDocument, {
    variables: { employerId, employeeId },
    throwOnError: true,
  });
  const data = rawData as EmployeeContributionRatesQuery | undefined;

  return (
    <ErrorBoundary name="fourOhOneKContributionsEditFlow">
      <PancakeStack>
        <PancakeStack.Header>
          <WorkflowHeader>
            <WorkflowHeader.Leading>
              <SambaAutoLink to={urlBuilder.dashboard()} aria-label="Back to dashboard">
                <Logo type="b4bSignature" />
              </SambaAutoLink>
            </WorkflowHeader.Leading>
            <WorkflowHeader.Title>Edit employee 401(k) contributions</WorkflowHeader.Title>
            <WorkflowHeader.Trailing>
              <IconLink name="X" label="Close" href={urlBuilder.employee({ employeeId })} />
            </WorkflowHeader.Trailing>
          </WorkflowHeader>
        </PancakeStack.Header>
        <PancakeStack.Main>
          <Separator css={{ position: 'absolute', left: '0' }} />
          <CenteredLayout size="large" css={{ padding: 'space.400', gap: 'space.150' }}>
            <H1 variant="pageTitle">Set employee 401(k) contributions.</H1>
            {data ? (
              <LoadedViewContent
                employeeName={employeeName}
                isEnrolledInAutoEscalation={isEnrolledInAutoEscalation}
                hasOutboundPayrollIntegration={hasOutboundPayrollIntegration}
                payrollProviderName={payrollProviderName}
                queryResponse={data}
              />
            ) : (
              <LoadingViewContent />
            )}
          </CenteredLayout>
        </PancakeStack.Main>
      </PancakeStack>
    </ErrorBoundary>
  );
};
